import { auth } from '~api';
import { getField, updateField } from 'vuex-map-fields';
import { Handler } from '~services';
import router from '~router';
import i18n from '../i18n';
import { encrypt, decrypt } from 'sjcl-all';

const LOADING = 'LOADING';
const SET_USER = 'SET_USER';

const localUser =
    localStorage.getItem('user') !== 'undefined'
        ? JSON.parse(localStorage.getItem('user'))
        : {};

const state = {
    isLoading: false,
    user: {
        ...localUser,
    },
};

const actions = {
    /**
     * Login user and set data to local storage
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    // eslint-disable-next-line no-unused-vars
    async login({ commit }, params) {
        // eslint-disable-next-line no-unused-vars
        let errorMessage = i18n.t('auth.login.errorMessage');

        const data = new FormData();
        data.append('username', params.username);
        data.append('password', params.password);

        // eslint-disable-next-line no-unused-vars
        const resp = await auth.login(data);

        if (resp.ok) {
            window.localStorage.setItem(
                'user',
                JSON.stringify({ ...resp.data, role: 'all' })
            );

            commit(SET_USER, data);

            let state_cyphertext = window.localStorage.getItem(
                resp.data.aes_key.slice(0, 4)
            );
            if (state_cyphertext != null) {
                let state_plaintext = decrypt(
                    resp.data.aes_key.slice(4),
                    state_cyphertext
                );
                let state_obj = JSON.parse(state_plaintext);
                for (var key in state_obj) {
                    window.localStorage.setItem(key, state_obj[key]);
                }
            }

            // You don't need the API key to peruse the API, just use let the session cookie do the auth.
            /*
            const respToken = await auth.getApiKey();

            if (respToken.ok) {
                $api.setHeader('Authorization', respToken.data);

                window.localStorage.setItem('token', respToken.data);
            }
            */
        }

        let RedirectPage = 'dashboard.portfolio';

        Handler.handleResponse(resp, undefined, errorMessage, RedirectPage);
    },

    /**
     * Change password and set data to local storage
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async confirmPassword({ commit }, data) {
        commit(LOADING);

        const resp = await auth.confirmPassword(data);

        Handler.handleResponse(resp, resp.data, resp.data, 'dashboard.markets');

        commit(LOADING);
    },

    /**
     * Confirm email
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async confirmEmail({ commit }, params) {
        const successMessage = i18n.t('auth.confirmEmail.successMessage');
        const errorMessage = i18n.t('auth.confirmEmail.errorMessage');
        let redirectLink = 'dashboard.markets';

        commit(LOADING);

        const resp = await auth.confirmEmail(params);

        Handler.handleResponse(
            resp,
            successMessage,
            errorMessage,
            redirectLink
        );

        commit(LOADING);
    },

    /**
     * Resend email
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async resendEmail({ commit }, email) {
        const successMessage = 'Confirmation email resent';
        const errorMessage = i18n.t('auth.confirmEmail.errorMessage');

        commit(LOADING);

        const resp = await auth.resendEmail({ email });

        Handler.handleResponse(resp, successMessage, errorMessage);

        commit(LOADING);
    },
    /**
     * Password reset
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async passwordReset({ commit }, email) {
        commit(LOADING);

        const data = new FormData();
        data.append('email', email);

        const resp = await auth.passwordSendEmail(data);

        Handler.handleResponse(resp, resp.data, resp.data, 'auth.login');

        commit(LOADING);
    },

    /**
     * Logout user and clear local storage
     * @param {function} commit
     */
    async logout() {
        const resp = await auth.logout();
        const keys_to_preserve = [
            'tt-list-instruments',
            'tt-list-date',
            'tt-secret',
            'tt-token',
            'publicKey',
            'privateKey',
        ];
        let state_obj = {};
        let user = JSON.parse(window.localStorage.getItem('user'));
        for (const key of keys_to_preserve) {
            let value = localStorage.getItem(key);
            if (value != null) {
                state_obj[key] = value;
                localStorage.removeItem(key);
            }
        }
        const state_plaintext = JSON.stringify(state_obj);
        let state_cyphertext = encrypt(user.aes_key.slice(4), state_plaintext);
        window.localStorage.setItem(user.aes_key.slice(0, 4), state_cyphertext);
        window.localStorage.removeItem('user'); // This gets returned on login and we want to see it updated.
        Handler.handleResponse(resp, undefined, undefined, 'auth.login');
    },

    /**
     * Register user and redirect to confirmation registration page
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async registration({ commit }, params) {
        commit(LOADING);
        const successMessage = i18n.t('auth.confirmEmail.successMessage');

        const data = new FormData();
        data.append('first_name', params.firstName);
        data.append('last_name', params.lastName);
        data.append('email', params.email);
        // data.append('phone', params.phone);
        // data.append('cell_phone', params.cellPhone);
        data.append('password', params.password);

        const resp = await auth.signUp(data);

        if (resp.ok) {
            router.push({
                name: 'auth.confirmRegistration'
            });
        }

        Handler.handleResponse(resp, successMessage);

        commit(LOADING);
    },
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    },
    [SET_USER](state, data) {
        state.user = data;
    },
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    user: (state) => state.user,
    isLoading: (state) => state.isLoading,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
